import React from 'react';
import {
  Platform,
  Pressable,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { ChevronRight } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';

export interface CompetitionWelcomeFooterStyles {
  ctaButton: StyleProp<ViewStyle>;
  ctaButtonPressed: StyleProp<ViewStyle>;
  ctaButtonHovered: StyleProp<ViewStyle>;
  ctaLabel: StyleProp<TextStyle>;
  dots: StyleProp<ViewStyle>;
  dot: StyleProp<ViewStyle>;
  dotActive: StyleProp<ViewStyle>;
}

export interface CompetitionWelcomeFooterProps {
  label: string;
  isExiting: boolean;
  phaseIndex: number;
  phaseCount: number;
  styles: CompetitionWelcomeFooterStyles;
  onPress: () => void;
  testID?: string;
}

/** Shared CTA + phase dots for the rules and points beats. */
export const CompetitionWelcomeFooter: React.FC<CompetitionWelcomeFooterProps> = ({
  label,
  isExiting,
  phaseIndex,
  phaseCount,
  styles,
  onPress,
  testID,
}) => {
  const theme = useTheme();

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        disabled={isExiting}
        onPress={onPress}
        style={({ pressed, hovered }) => [
          styles.ctaButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && !isExiting && styles.ctaButtonPressed,
          hovered && Platform.OS === 'web' && styles.ctaButtonHovered,
        ]}
        testID={testID}
      >
        <Text style={styles.ctaLabel}>{label}</Text>
        <ChevronRight size={16} color={theme.onPrimary} strokeWidth={2.5} />
      </Pressable>

      <View style={styles.dots}>
        {Array.from({ length: phaseCount }, (_, i) => (
          <View key={i} style={[styles.dot, i === phaseIndex && styles.dotActive]} />
        ))}
      </View>
    </>
  );
};
